"use client";

// 2026-09-08 construcción progresiva del landing (websearch 2026: docs GSAP
// ScrollTrigger.batch + patrones 2026 — timeline por sección con el título
// primero y cascada con stagger ~0.08, start "top 85%", once (sin reversa),
// solo opacity/transform en el compositor). Sin GSAP a propósito:
// IntersectionObserver + keyframes CSS = 0 bytes de JS, no depende del chunk
// de gsap y respeta el budget free-tier. Sin JS el contenido queda visible
// (la clase inicial solo la pone el efecto) y con prefers-reduced-motion el
// CSS fuerza el estado final sin animar.

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { splitAccentLastWord } from "@/components/chat/chat-greeting";

export function Reveal({
  children,
  className,
  /** escalonado de la cascada dentro de la sección */
  delay = 0,
  id,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  id?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // SSR/tests/navegadores viejos: visible sin animar (deferido para no
    // encadenar renders dentro del efecto).
    if (typeof IntersectionObserver === "undefined") {
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    }
    el.classList.add("reveal");
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          io.disconnect();
          setVisible(true);
        }
      },
      // dispara cuando el bloque asoma (~top 85% del viewport), una sola vez
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (visible) ref.current?.classList.add("reveal-in");
  }, [visible]);

  const style = { "--reveal-delay": `${delay}ms` } as CSSProperties;
  return (
    <div ref={ref} id={id} className={className} style={style}>
      {children}
    </div>
  );
}

/** Acento de cola below-fold con la misma regla que el saludo del chat
 *  (paridad `splitAccentLastWord` de chat-greeting.tsx:100-110 — SSOT del
 *  corte "última palabra"; este componente reutiliza la regla, no la duplica).
 *  2026-10-03 RUN12 [design-chroma-flow-parity-breach-reveal-accent-tail]:
 *  el corte anterior (`split(" ")` + `slice(-2)` CJK) partía el shaping árabe
 *  a mitad de palabra y dejaba el acento en partículas CJK (です/か), donde
 *  el color se lee como error y no como jerarquía (research auditor: Utopia
 *  fluid type + Intl.Segmenter word/grapheme Baseline 2026-04-08 — zh/ja no
 *  usan espacios, así que `split(" ")` no segmenta — + WCAG 2.2 AA 1.4.3: el
 *  gradiente se juzga en el peor punto). Regla: con espacios → acento en la
 *  última palabra (idéntico visual LTR actual); sin espacios (zh/ja/token) →
 *  SIN acento deliberado, la jerarquía la da el peso del h2 padre
 *  (semibold/bold, paridad greeting/private-hero). `dir="auto"` en el
 *  envoltorio para que el tramo tome la dirección de su primer carácter
 *  fuerte en ar sin espejar el layout (paridad ChatGreeting).
 *  2026-09-18 FASE 3: variante OPT-IN del chroma — reservada para acentos
 *  below-fold; el H1 del hero usa .text-chroma-flow en heroTitleB por decisión
 *  dueño 2026-09-19 (paridad aprobada, ver Hero.tsx).
 *  Sin keys nuevas de i18n: funciona en las 14 locales y el textContent del
 *  h2 queda idéntico (contratos de tests intactos). */
export function AccentTail({ text }: { text: string }) {
  const { plain, accent } = splitAccentLastWord(text);
  if (!accent) return <span dir="auto">{text}</span>;
  return (
    <span dir="auto">
      {plain}
      <span className="text-chroma-flow">{accent}</span>
    </span>
  );
}
